/**
 * JobList Component
 * Displays list of jobs with filtering and actions using modern Tailwind CSS
 */

import React, { useState, useEffect } from 'react';
import { Job, JobCategory, JobStatus } from '../../types/job';
import { JobStatusBadge } from './JobStatusBadge';
import { Search, Edit2, Trash2, ChevronDown, ChevronUp, Download, ChevronRight, Code, X, Copy } from 'lucide-react';
import { auth } from '../../auth/supabaseClient';

interface JobListProps {
  jobs: Job[];
  onEdit: (job: Job) => void;
  onDelete: (jobId: string) => Promise<void>;
  onSelect: (job: Job) => void;
  selectedJobId?: string;
  loading?: boolean;
}

export const JobList: React.FC<JobListProps> = ({
  jobs,
  onEdit,
  onDelete,
  onSelect,
  selectedJobId,
  loading = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<JobCategory[]>([]);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandedParentJobs, setExpandedParentJobs] = useState<Set<string>>(new Set());
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [selectedJobForApi, setSelectedJobForApi] = useState<Job | null>(null);
  const [authToken, setAuthToken] = useState<string>('');
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Get current auth token from Supabase
  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const {
          data: { session },
        } = await auth.getSession();
        if (session?.access_token) {
          setAuthToken(session.access_token);
        }
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };

    getAuthToken();
  }, []);

  // Filter jobs based on current filters
  const filteredJobs = jobs.filter((job) => {
    if (statusFilter.length > 0 && !statusFilter.includes(job.status)) {
      return false;
    }
    if (categoryFilter.length > 0 && !categoryFilter.includes(job.category)) {
      return false;
    }
    if (searchText && !job.name.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Separate parent jobs and their children
  const parentJobs = filteredJobs.filter((job) => (job as any).isParentJob);
  
  const getChildJobs = (parentJobName: string) => {
    return filteredJobs.filter(
      (job) => (job as any).isChildJob && (job as any).parentJobName === parentJobName
    );
  };

  // Jobs that are not parent or child jobs (standalone)
  const standaloneJobs = filteredJobs.filter(
    (job) => !(job as any).isParentJob && !(job as any).isChildJob
  );

  // Debug logging
  React.useEffect(() => {
    if (jobs.length > 0) {
      console.log('Jobs loaded:', {
        total: jobs.length,
        parentJobs: parentJobs.length,
        standaloneJobs: standaloneJobs.length,
        jobsWithParentFlag: jobs.filter((j: any) => j.isParentJob).length,
        jobsWithChildFlag: jobs.filter((j: any) => j.isChildJob).length,
        sample: jobs[0],
      });
    }
  }, [jobs, parentJobs.length, standaloneJobs.length]);

  const handleStatusFilterChange = (status: JobStatus) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleCategoryFilterChange = (category: JobCategory) => {
    setCategoryFilter((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleDelete = async (jobId: string) => {
    if (confirm('Are you sure you want to delete this job?')) {
      await onDelete(jobId);
    }
  };

  const toggleJobSelection = (jobId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const newSet = new Set(selectedJobIds);
    if (newSet.has(jobId)) {
      newSet.delete(jobId);
    } else {
      newSet.add(jobId);
    }
    setSelectedJobIds(newSet);
    
    // Update selectAll state based on whether all selectable jobs are selected
    const selectableJobs = filteredJobs.filter(
      (job) => !(job as any).isChildJob
    );
    const allSelected = selectableJobs.length > 0 && selectableJobs.every(job => newSet.has(job.id));
    setSelectAll(allSelected);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedJobIds(new Set());
      setSelectAll(false);
    } else {
      // Select all parent and standalone jobs (not children)
      const selectableJobs = filteredJobs.filter(
        (job) => !(job as any).isChildJob
      );
      setSelectedJobIds(new Set(selectableJobs.map(job => job.id)));
      setSelectAll(true);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedJobIds.size === 0) {
      return;
    }
    
    const count = selectedJobIds.size;
    if (confirm(`Are you sure you want to delete ${count} job(s)? This will also delete all child tasks.`)) {
      for (const jobId of selectedJobIds) {
        await onDelete(jobId);
      }
      setSelectedJobIds(new Set());
      setSelectAll(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const toggleParentExpanded = (jobId: string) => {
    const newSet = new Set(expandedParentJobs);
    if (newSet.has(jobId)) {
      newSet.delete(jobId);
    } else {
      newSet.add(jobId);
    }
    setExpandedParentJobs(newSet);
  };

  // Get current API base URL
  const getApiBaseUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:3000';
    return window.location.origin;
  };

  const renderJobRow = (job: Job, isChild: boolean = false) => (
    <tr
      key={job.id}
      className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors ${
        selectedJobId === job.id ? 'bg-blue-50' : ''
      } ${isChild ? 'bg-gray-50' : ''}`}
      onClick={() => onSelect(job)}
    >
      <td className="px-4 py-4">
        <input
          type="checkbox"
          checked={selectedJobIds.has(job.id)}
          onChange={(e) => toggleJobSelection(job.id, e as any)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {isChild && <span className="text-gray-400 w-5"></span>}
          <p className="text-sm font-medium text-gray-900">{job.name}</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-gray-600">{job.templateId}</p>
      </td>
      <td className="px-6 py-4">
        <JobStatusBadge status={job.status} />
      </td>
      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(job.createdAt)}</td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedJobForApi(job);
              setApiModalOpen(true);
            }}
            className="text-purple-600 hover:text-purple-800 transition-colors p-1"
            title="API Documentation"
          >
            <Code className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(job);
            }}
            className="text-blue-600 hover:text-blue-800 transition-colors p-1"
            title="Edit job"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(job.id);
            }}
            className="text-red-600 hover:text-red-800 transition-colors p-1"
            title="Delete job"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      {/* Filter Panel */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
        
        <div className="space-y-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Jobs</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by job name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
              {Object.values(JobStatus).map((status) => (
                <label key={status} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(status)}
                    onChange={() => handleStatusFilterChange(status)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {Object.values(JobCategory).map((category) => (
                <label key={category} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={categoryFilter.includes(category)}
                    onChange={() => handleCategoryFilterChange(category)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{category.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        {/* Selection Action Bar */}
        {selectedJobIds.size > 0 && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center justify-between">
            <span className="text-sm text-blue-900 font-medium">
              {selectedJobIds.size} job{selectedJobIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        )}

        {filteredJobs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">
              {jobs.length === 0 ? 'No jobs created yet. Create your first job to get started.' : 'No jobs match the current filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      title="Select all jobs"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Template</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Parent Jobs with Children */}
                {parentJobs.map((parentJob) => {
                  const children = getChildJobs(parentJob.name);
                  const isExpanded = expandedParentJobs.has(parentJob.id);
                  
                  return (
                    <React.Fragment key={parentJob.id}>
                      <tr 
                        className={`hover:bg-gray-50 transition-colors ${selectedJobId === parentJob.id ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedJobIds.has(parentJob.id)}
                            onChange={(e) => toggleJobSelection(parentJob.id, e as any)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleParentExpanded(parentJob.id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                            >
                              <ChevronRight className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>
                            <p className="font-medium text-gray-900">{parentJob.name}</p>
                            <span className="text-xs text-gray-500 ml-2">({children.length} tasks)</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-600">{parentJob.templateId}</p>
                        </td>
                        <td className="px-6 py-4">
                          <JobStatusBadge status={parentJob.status} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(parentJob.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedJobForApi(parentJob);
                                setApiModalOpen(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 transition-colors p-1"
                              title="API Documentation"
                            >
                              <Code className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(parentJob);
                              }}
                              className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                              title="Edit job"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(parentJob.id);
                              }}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Delete job"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Child Jobs */}
                      {isExpanded && children.map((childJob) => (
                        <tr
                          key={childJob.id}
                          onClick={() => onSelect(childJob)}
                          className={`border-l-4 border-l-blue-400 bg-gray-50 hover:bg-blue-50 transition-colors cursor-pointer ${
                            selectedJobId === childJob.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedJobIds.has(childJob.id)}
                              onChange={(e) => toggleJobSelection(childJob.id, e as any)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4 pl-12">
                            <p className="text-sm text-gray-900">{childJob.name}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600">{childJob.templateId}</p>
                          </td>
                          <td className="px-6 py-4">
                            <JobStatusBadge status={childJob.status} />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDate(childJob.createdAt)}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedJobForApi(childJob);
                                  setApiModalOpen(true);
                                }}
                                className="text-purple-600 hover:text-purple-800 transition-colors p-1"
                                title="API Documentation"
                              >
                                <Code className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(childJob);
                                }}
                                className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                                title="Edit job"
                              >
                                <Edit2 className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(childJob.id);
                                }}
                                className="text-red-600 hover:text-red-800 transition-colors p-1"
                                title="Delete job"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Parent Job Details */}
                      {expandedJobId === parentJob.id && (
                        <tr className="bg-gray-50 border-t-2 border-blue-200">
                          <td colSpan={5} className="px-6 py-4">
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Category</p>
                                  <p className="mt-1 text-sm text-gray-900 capitalize">{parentJob.category.replace(/_/g, ' ')}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Schedule</p>
                                  <p className="mt-1 text-sm text-gray-900 capitalize">{parentJob.condition}</p>
                                </div>
                              </div>

                              {parentJob.description && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Description</p>
                                  <p className="mt-1 text-sm text-gray-700">{parentJob.description}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Standalone Jobs */}
                {standaloneJobs.map((job) => (
                  renderJobRow(job, false)
                ))}
                
                {/* Fallback: Show all jobs if no parent/standalone jobs found (for debugging) */}
                {parentJobs.length === 0 && standaloneJobs.length === 0 && filteredJobs.length > 0 && (
                  <>
                    <tr className="bg-yellow-50 border-b-2 border-yellow-300">
                      <td colSpan={5} className="px-6 py-4 text-sm text-yellow-800">
                        ⚠️ Job display issue detected. Showing all jobs:
                      </td>
                    </tr>
                    {filteredJobs.map((job) => (
                      renderJobRow(job, false)
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {jobs.length > 0 && (
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium">{parentJobs.length + standaloneJobs.length}</span> of <span className="font-medium">{jobs.filter((j: any) => !(j as any).isChildJob).length}</span> jobs
        </div>
      )}

      {/* API Documentation Modal */}
      {apiModalOpen && selectedJobForApi && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setApiModalOpen(false)}
          />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-white">
                <h3 className="text-xl font-semibold text-gray-900">API Documentation</h3>
                <button
                  type="button"
                  onClick={() => setApiModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Security Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">🔐 Security Required</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    All API requests require Supabase authentication. Your current access token is already loaded below - just copy and paste the examples!
                  </p>
                  <div>
                    <p className="text-xs font-medium text-blue-900 mb-1">Your Access Token:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 break-all border border-blue-200 max-h-16 overflow-y-auto">
                        {authToken || 'Loading token...'}
                      </code>
                      <button
                        type="button"
                        onClick={() => authToken && navigator.clipboard.writeText(authToken)}
                        className="text-blue-600 hover:text-blue-800 p-1 flex-shrink-0"
                        title="Copy token"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-3">
                    💡 Admin users can access all files. Other users can only access public files or files they created.
                  </p>
                </div>

                {/* Job Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Job ID</h4>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono text-gray-700 break-all">
                      {selectedJobForApi.id}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(selectedJobForApi.id)}
                      className="text-gray-500 hover:text-gray-700 p-1"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Download Input File */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">📥 Download Input File</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Download the input file that needs to be processed. File must be marked as public or you must be the file owner.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Endpoint:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono text-gray-700 break-all border border-gray-200">
                          GET /api/files/download/{selectedJobForApi.inputFileId || '{fileId}'}
                        </code>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(`GET /api/files/download/${selectedJobForApi.inputFileId}`)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Example cURL:</p>
                      <div className="flex items-start gap-2">
                        <code className="flex-1 block bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 border border-gray-200 overflow-x-auto">
                          curl -X GET "{getApiBaseUrl()}/api/files/download/{selectedJobForApi.inputFileId || 'fileId'}" \<br />
                          &nbsp;&nbsp;-H "Authorization: Bearer {authToken || 'YOUR_TOKEN'}" \<br />
                          &nbsp;&nbsp;-o input_file
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            const cmd = `curl -X GET "${getApiBaseUrl()}/api/files/download/${selectedJobForApi.inputFileId}" -H "Authorization: Bearer ${authToken}" -o input_file`;
                            navigator.clipboard.writeText(cmd);
                          }}
                          className="text-gray-500 hover:text-gray-700 p-1 flex-shrink-0 mt-1"
                          title="Copy command"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upload Output File */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">📤 Upload Processed File</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    After processing the input file, upload the result using this endpoint:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Endpoint:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono text-gray-700 break-all border border-gray-200">
                          PUT /api/jobs/{selectedJobForApi.id}/result
                        </code>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(`PUT /api/jobs/${selectedJobForApi.id}/result`)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Headers:</p>
                      <code className="block bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 border border-gray-200">
                        Content-Type: multipart/form-data
                      </code>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Form Data:</p>
                      <code className="block bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 border border-gray-200">
                        file: [processed_file_binary_data]
                      </code>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Example cURL:</p>
                      <code className="block bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 border border-gray-200 overflow-x-auto">
                        curl -X PUT "{getApiBaseUrl()}/api/jobs/{selectedJobForApi.id}/result" \<br />
                        &nbsp;&nbsp;-H "Authorization: Bearer YOUR_SUPABASE_TOKEN" \<br />
                        &nbsp;&nbsp;-F "file=@processed_output_file"
                      </code>
                    </div>
                  </div>
                </div>

                {/* Job Details */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">📋 Job Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">Name</p>
                      <p className="text-gray-900">{selectedJobForApi.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Status</p>
                      <p className="text-gray-900 capitalize">{selectedJobForApi.status}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Template</p>
                      <p className="text-gray-900">{selectedJobForApi.templateId}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Input File ID</p>
                      <p className="text-gray-900 font-mono text-xs truncate">{selectedJobForApi.inputFileId || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <div className="border-t pt-6">
                  <button
                    type="button"
                    onClick={() => setApiModalOpen(false)}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
